import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import VehicleIcon from './VehicleIcons';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

// Route-replay map: draws the recorded/approximate route on a coastline map
// while the vehicle glyph travels along it and visited places pop in.
// Route data lives in src/data/routes/*.json (lazy-loaded chunks).

const VIEW_W = 800;
const MIN_VIEW_H = 300;
const MAX_VIEW_H = 760;

function buildProjection(bbox) {
  const [w, s, e, n] = bbox;
  const kx = Math.cos((((s + n) / 2) * Math.PI) / 180);
  const spanX = (e - w) * kx;
  const spanY = n - s;
  const scale = VIEW_W / spanX;
  const rawH = spanY * scale;
  const viewH = Math.max(MIN_VIEW_H, Math.min(MAX_VIEW_H, rawH));
  const scaleY = viewH / spanY;
  const scaleUsed = Math.min(scale, scaleY);
  const offsetX = (VIEW_W - spanX * scaleUsed) / 2;
  const offsetY = (viewH - spanY * scaleUsed) / 2;

  return {
    viewH,
    toX: (lon) => offsetX + (lon - w) * kx * scaleUsed,
    toY: (lat) => offsetY + (n - lat) * scaleUsed,
  };
}

function toPathD(points, toX, toY) {
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p[0]).toFixed(1)} ${toY(p[1]).toFixed(1)}`)
    .join(' ');
}

// Route legs are measured here in JS instead of with SVG getTotalLength().
// Safari's rendered dash lengths don't match its own getTotalLength() on long
// paths, so the old dasharray/dashoffset reveal leaked later stretches of the
// route onto the map early. Drawing a growing prefix path is engine-agnostic.
function buildPolyline(points, toX, toY) {
  const xy = points.map((p) => [Number(toX(p[0]).toFixed(1)), Number(toY(p[1]).toFixed(1))]);
  const cum = [0];
  const fragEnd = [];
  let d = '';
  xy.forEach(([x, y], i) => {
    d += `${i === 0 ? 'M' : ' L'}${x} ${y}`;
    fragEnd.push(d.length);
    if (i > 0) {
      const [px, py] = xy[i - 1];
      cum.push(cum[i - 1] + Math.hypot(x - px, y - py));
    }
  });
  return { xy, cum, fragEnd, d, total: cum[cum.length - 1] };
}

// Index of the last vertex at or before `dist` along the polyline.
function vertexBefore(poly, dist) {
  let lo = 0;
  let hi = poly.cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (poly.cum[mid] <= dist) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

function pointAt(poly, dist) {
  if (dist <= 0 || poly.xy.length === 1) {
    return poly.xy[0];
  }
  if (dist >= poly.total) {
    return poly.xy[poly.xy.length - 1];
  }
  const k = vertexBefore(poly, dist);
  const span = poly.cum[k + 1] - poly.cum[k];
  const f = span > 0 ? (dist - poly.cum[k]) / span : 0;
  const [ax, ay] = poly.xy[k];
  const [bx, by] = poly.xy[k + 1];
  return [ax + (bx - ax) * f, ay + (by - ay) * f];
}

function prefixD(poly, dist) {
  if (dist <= 0) {
    return '';
  }
  if (dist >= poly.total) {
    return poly.d;
  }
  const k = vertexBefore(poly, dist);
  const [x, y] = pointAt(poly, dist);
  return `${poly.d.slice(0, poly.fragEnd[k])} L${x.toFixed(1)} ${y.toFixed(1)}`;
}

function easeInOutSine(t) {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

function TravelMap({ routeId, routeKind, cities = [], vehicle, language, variant = 'full' }) {
  const [routeData, setRouteData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [playState, setPlayState] = useState('idle');
  const [visited, setVisited] = useState(() => new Set());
  // {type, n}: n bumps on every switch so the pop/spark animation remounts.
  const [vehicleSwap, setVehicleSwap] = useState({ type: vehicle, n: 0 });
  const prefersReducedMotion = usePrefersReducedMotion();

  const segRefs = useRef([]);
  const vehicleRef = useRef(null);
  const frameRef = useRef(null);
  const loopTimerRef = useRef(null);
  const playRef = useRef(null);
  const isThumb = variant === 'thumb';
  // 'teaser': a compact, label-free preview that replays on a loop.
  const isTeaser = variant === 'teaser';

  useEffect(() => {
    setVehicleSwap((prev) => (prev.type === vehicle ? prev : { type: vehicle, n: prev.n + 1 }));
  }, [vehicle]);

  const swapVehicle = useCallback((nextType) => {
    setVehicleSwap((prev) => (prev.type === nextType ? prev : { type: nextType, n: prev.n + 1 }));
  }, []);

  useEffect(() => {
    let alive = true;
    import(`../data/routes/${routeId}.json`)
      .then((mod) => {
        if (alive) {
          setRouteData(mod.default || mod);
        }
      })
      .catch(() => {
        if (alive) {
          setLoadError(true);
        }
      });

    return () => {
      alive = false;
    };
  }, [routeId]);

  const geo = useMemo(() => {
    if (!routeData) {
      return null;
    }

    const { toX, toY, viewH } = buildProjection(routeData.bbox);
    return {
      viewH,
      toX,
      toY,
      landD: routeData.land.map((ring) => `${toPathD(ring, toX, toY)} Z`).join(' '),
      segments: routeData.segments.map((seg) => ({
        poly: buildPolyline(seg.pts, toX, toY),
        mode: seg.mode || null,
        // ghost legs: the vehicle rides them but no line is drawn (return
        // halves of bus side-trips — overlapping dashes would look solid).
        ghost: Boolean(seg.ghost),
      })),
      gapDs: (routeData.gaps || []).map((gap) => toPathD(gap, toX, toY)),
      cityPts: cities.map((city) => ({ ...city, x: toX(city.lon), y: toY(city.lat) })),
      start: routeData.segments[0]?.pts?.[0],
    };
  }, [cities, routeData]);

  const stopAnimation = useCallback(() => {
    if (frameRef.current) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    window.clearTimeout(loopTimerRef.current);
  }, []);

  const showFinal = useCallback(() => {
    stopAnimation();
    if (!geo) {
      return;
    }
    geo.segments.forEach((seg, i) => {
      const el = segRefs.current[i];
      if (el) {
        el.setAttribute('d', seg.poly.d);
        el.style.strokeDasharray = routeKind === 'approx' ? '6 7' : 'none';
      }
    });
    const lastSeg = geo.segments[geo.segments.length - 1];
    if (vehicleRef.current && lastSeg) {
      const [x, y] = pointAt(lastSeg.poly, lastSeg.poly.total);
      vehicleRef.current.setAttribute('transform', `translate(${x} ${y})`);
      vehicleRef.current.style.opacity = '1';
    }
    swapVehicle(lastSeg?.mode || vehicle);
    setVisited(new Set(cities.map((_, i) => i)));
    setPlayState('done');
  }, [cities, geo, routeKind, stopAnimation, swapVehicle, vehicle]);

  const play = useCallback(() => {
    if (!geo || prefersReducedMotion || geo.segments.length === 0) {
      showFinal();
      return;
    }

    stopAnimation();
    const segs = geo.segments;
    const lengths = segs.map((seg) => seg.poly.total);
    const total = lengths.reduce((a, b) => a + b, 0);
    const prefix = [];
    lengths.reduce((acc, len, i) => {
      prefix[i] = acc;
      return acc + len;
    }, 0);

    // Where along the route each city gets "visited": the FIRST close
    // approach, not the globally nearest point — places passed early must
    // pop early even if the route later comes back slightly closer.
    const cityMarks = geo.cityPts.map((city) => {
      const samples = [];
      let bestD2 = Infinity;
      segs.forEach((seg, i) => {
        const len = lengths[i];
        const step = Math.max(4, len / 160);
        for (let l = 0; l <= len; l += step) {
          const [x, y] = pointAt(seg.poly, l);
          const d2 = (x - city.x) ** 2 + (y - city.y) ** 2;
          samples.push({ at: prefix[i] + l, d2 });
          if (d2 < bestD2) {
            bestD2 = d2;
          }
        }
      });
      const threshold = Math.max(bestD2 * 1.6, 26 * 26);
      const hit = samples.find((sample) => sample.d2 <= threshold);
      return hit ? hit.at : 0;
    });

    // Per-leg draw state, so each frame only rewrites the leg that moved.
    const drawnState = segs.map(() => -1);
    const drawLeg = (i, dist) => {
      const el = segRefs.current[i];
      const clamped = Math.max(0, Math.min(dist, lengths[i]));
      if (!el || drawnState[i] === clamped) {
        return;
      }
      drawnState[i] = clamped;
      el.setAttribute('d', prefixD(segs[i].poly, clamped));
    };
    segs.forEach((_, i) => {
      const el = segRefs.current[i];
      if (el) {
        el.style.strokeDasharray = 'none';
      }
      drawLeg(i, 0);
    });

    setVisited(new Set());
    setPlayState('playing');

    const duration = isTeaser ? 6500 : Math.min(13000, Math.max(6500, total * 5.2));
    // Accumulated-time clock so the vehicle can pause when it changes ride.
    let elapsed = 0;
    let lastNow = performance.now();
    let pauseUntil = 0;
    let currentMode = segs[0].mode || vehicle;
    swapVehicle(currentMode);

    const tick = (now) => {
      const dt = Math.max(0, Math.min(64, now - lastNow));
      lastNow = now;
      if (now >= pauseUntil) {
        elapsed += dt;
      }
      const rawT = Math.min(1, elapsed / duration);
      const t = easeInOutSine(rawT);
      const drawn = t * total;

      // The leg the vehicle is on: the last one whose start has been reached.
      let activeIndex = 0;
      for (let i = 0; i < segs.length; i += 1) {
        drawLeg(i, drawn - prefix[i]);
        if (drawn >= prefix[i]) {
          activeIndex = i;
        }
      }

      const poly = segs[activeIndex].poly;
      const local = Math.max(0, Math.min(drawn - prefix[activeIndex], lengths[activeIndex]));
      const [x, y] = pointAt(poly, local);
      const [aheadX] = pointAt(poly, Math.min(local + 3, lengths[activeIndex]));
      const flip = aheadX < x ? -1 : 1;
      if (vehicleRef.current) {
        vehicleRef.current.setAttribute('transform', `translate(${x} ${y}) scale(${flip} 1)`);
        vehicleRef.current.style.opacity = '1';
      }
      // Ferry / bus / train legs swap the traveling glyph. On a switch the
      // vehicle stops for a beat and the new glyph pops in with a spark.
      const nextMode = segs[activeIndex].mode || vehicle;
      if (nextMode !== currentMode) {
        currentMode = nextMode;
        pauseUntil = now + 620;
        swapVehicle(nextMode);
      }

      setVisited((prev) => {
        let changed = false;
        const next = new Set(prev);
        cityMarks.forEach((mark, i) => {
          if (drawn >= mark - 2 && !next.has(i)) {
            next.add(i);
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      if (rawT < 1) {
        frameRef.current = window.requestAnimationFrame(tick);
      } else {
        frameRef.current = null;
        segs.forEach((seg, i) => {
          const el = segRefs.current[i];
          if (el) {
            el.setAttribute('d', seg.poly.d);
            el.style.strokeDasharray = routeKind === 'approx' ? '6 7' : 'none';
          }
        });
        setVisited(new Set(cities.map((_, i) => i)));
        setPlayState('done');
        if (isTeaser) {
          loopTimerRef.current = window.setTimeout(() => playRef.current?.(), 1600);
        }
      }
    };

    frameRef.current = window.requestAnimationFrame(tick);
  }, [cities, geo, isTeaser, prefersReducedMotion, routeKind, showFinal, stopAnimation, swapVehicle, vehicle]);

  playRef.current = play;

  // Auto-play once the map data is ready (full variant only).
  useEffect(() => {
    if (!geo) {
      return undefined;
    }

    if (isThumb || prefersReducedMotion) {
      showFinal();
      return undefined;
    }

    const timer = window.setTimeout(() => {
      play();
    }, 450);

    return () => {
      window.clearTimeout(timer);
      stopAnimation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, isThumb, prefersReducedMotion]);

  const copy =
    language === 'ja'
      ? {
          recorded: 'GPSの行程実績',
          approx: '概略ルート',
          replay: 'もう一度再生',
          loading: '地図を読み込み中…',
          error: '地図を読み込めませんでした。',
        }
      : {
          recorded: 'Recorded GPS track',
          approx: 'Approximate route',
          replay: 'Replay',
          loading: 'Loading map…',
          error: 'Could not load the map.',
        };

  if (loadError) {
    return <p className="travel-map__status mono">{copy.error}</p>;
  }

  if (!geo) {
    return <p className="travel-map__status mono">{copy.loading}</p>;
  }

  return (
    <div className={`travel-map ${isThumb ? 'travel-map--thumb' : ''} ${isTeaser ? 'travel-map--teaser' : ''}`}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${geo.viewH}`}
        className="travel-map__svg"
        role="img"
        aria-label={
          language === 'ja'
            ? `旅のルート地図（${routeKind === 'approx' ? copy.approx : copy.recorded}）。経由地: ${cities
                .map((city) => city.ja)
                .join('、')}`
            : `Journey route map (${routeKind === 'approx' ? copy.approx : copy.recorded}). Waypoints: ${cities
                .map((city) => city.en)
                .join(', ')}`
        }
      >
        <path className="travel-map__land" d={geo.landD} />

        {geo.gapDs.map((d, i) => (
          <path key={`gap-${i}`} className="travel-map__gap" d={d} />
        ))}

        {geo.segments.map((seg, i) => (
          <path
            key={`seg-${i}`}
            className={`travel-map__route ${routeKind === 'approx' ? 'is-approx' : ''} ${
              seg.mode ? `travel-map__route--${seg.mode}` : ''
            } ${seg.ghost ? 'travel-map__route--ghost' : ''}`}
            d={seg.poly.d}
            ref={(el) => {
              segRefs.current[i] = el;
            }}
          />
        ))}

        {geo.start ? (
          <circle className="travel-map__start" cx={geo.toX(geo.start[0])} cy={geo.toY(geo.start[1])} r="4" />
        ) : null}

        {geo.cityPts.map((city, i) => {
          // Labels flip near the right edge, or when the data asks (dense
          // clusters); labelDy/labelDx nudge labels apart. When the nudge is
          // large, a thin leader line ties the label back to its dot.
          const flipLabel = city.labelFlip || city.x > VIEW_W * 0.84;
          const labelDx = city.labelDx || 0;
          const labelDy = city.labelDy || 0;
          const labelX = (flipLabel ? -9 : 9) + labelDx;
          const labelY = -7 + labelDy;
          const needsLeader = Math.abs(labelDx) + Math.abs(labelDy) >= 15;
          const isPeak = city.kind === 'peak';
          return (
            <g
              key={city.en}
              className={`travel-map__city ${isPeak ? 'travel-map__city--peak' : ''} ${
                visited.has(i) ? 'is-visited' : ''
              }`}
              transform={`translate(${city.x} ${city.y})`}
            >
              {isPeak ? <path d="M0 -5.2 L4.8 3.4 H-4.8 Z" /> : <circle r="3.4" />}
              <g className="travel-map__city-label">
                {needsLeader ? (
                  <line
                    className="travel-map__leader"
                    x1={flipLabel ? -4.5 : 4.5}
                    y1={labelY < -7 ? -3 : labelY > -7 ? 3 : 0}
                    x2={labelX + (flipLabel ? 2.5 : -2.5)}
                    y2={labelY - 3.5}
                  />
                ) : null}
                <text x={labelX} y={labelY} textAnchor={flipLabel ? 'end' : 'start'}>
                  {language === 'ja' ? city.ja : city.en}
                </text>
              </g>
            </g>
          );
        })}

        <g ref={vehicleRef} className="travel-map__vehicle" style={{ opacity: 0 }}>
          <circle r="14" className="travel-map__vehicle-bg" />
          {/* Remounting on every switch replays the pop + sun-ray burst. */}
          <g key={`${vehicleSwap.type}-${vehicleSwap.n}`} className="travel-map__vehicle-pop">
            <g className="travel-map__vehicle-sparks" aria-hidden="true">
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                <line
                  key={angle}
                  x1="0"
                  y1="-17"
                  x2="0"
                  y2="-24"
                  style={{ '--spark-angle': `${angle}deg` }}
                />
              ))}
            </g>
            <VehicleIcon type={vehicleSwap.type} x="-10" y="-10" width="20" height="20" strokeWidth="1.7" />
          </g>
        </g>
      </svg>

      {isTeaser ? null : (
        <div className="travel-map__meta mono">
          <span className={`travel-map__kind ${routeKind === 'approx' ? 'is-approx' : ''}`}>
            {routeKind === 'approx' ? copy.approx : copy.recorded}
          </span>
          {!isThumb ? (
            <button
              type="button"
              className="travel-map__replay chip"
              onClick={play}
              disabled={playState === 'playing'}
            >
              ↻ {copy.replay}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function TravelMapModal({ project, language, onClose }) {
  const closeButtonRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const container = contentRef.current;
        if (!container) {
          return;
        }
        const focusables = container.querySelectorAll(
          'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) {
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (event.shiftKey) {
          if (active === first || !container.contains(active)) {
            event.preventDefault();
            last.focus();
          }
        } else if (active === last || !container.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  const title = language === 'ja' ? project.title_ja : project.title_en;
  const period = language === 'ja' ? project.period_ja : project.period_en;

  return createPortal(
    <div className="map-modal__overlay" onClick={onClose} role="presentation">
      <div
        className="map-modal__content"
        ref={contentRef}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="map-modal__head">
          <div>
            <p className="map-modal__period mono">{period}</p>
            <h3 className="map-modal__title">{title}</h3>
          </div>
          <button
            type="button"
            ref={closeButtonRef}
            onClick={onClose}
            className="close-modal"
            aria-label={language === 'ja' ? '閉じる' : 'Close'}
          >
            {language === 'ja' ? '閉じる' : 'Close'}
          </button>
        </header>

        <TravelMap
          routeId={project.route}
          routeKind={project.routeKind}
          cities={project.cities}
          vehicle={project.vehicle}
          language={language}
        />
      </div>
    </div>,
    document.body
  );
}

export default TravelMap;
